import { ValidatePinProps, Consumer, SendPinProps } from 'store/profile/types';
import { updateConsumerEmail, updateConsumerPhone } from 'store/profile/actions';
import { RootDispatch, Thunk } from 'store/types';
import { cleanPhone } from 'modules/utils/PhoneUtils';
import { getAge } from 'lib/preRegistration';
import { CheckListItem, PatientInfo, PatientPhone, UpdateSkipPaymentFlagPayload } from './types';

export const PRE_REGISTRATION = 'PRE_REGISTRATION';
export const PRE_REGISTRATION_SUCCESS = 'PRE_REGISTRATION_SUCCESS';
export const PRE_REGISTRATION_FAIL = 'PRE_REGISTRATION_FAIL';
export const PRE_REGISTRATION_RESET = 'PRE_REGISTRATION_RESET';
export const PRE_REGISTRATION_DESTROY_SESSION = 'PRE_REGISTRATION_DESTROY_SESSION';

export const PRE_REGISTRATION_JWT_TOKEN = 'PRE_REGISTRATION_JWT_TOKEN';

export const UPDATE_PRE_REGISTRATION_CHECKLIST = 'PRE_REGISTRATION_UPDATE_CHECKLIST';
export const UPDATE_PRE_REGISTRATION_CHECKLIST_STEP = 'PRE_REGISTRATION_UPDATE_CHECKLIST_STEP';
export const UPDATE_SKIP_PAYMENT_FLAG = 'PRE_REGISTRATION_UPDATE_SKIP_PAYMENT_FLAG';
export const UPDATE_SELF_PAYMENT_FLAG = 'PRE_REGISTRATION_UPDATE_SELF_PAYMENT_FLAG';
export const UPDATE_PROCESS_STATUS = 'PRE_REGISTRATION_UPDATE_PROCESS_STATUS';
export const UPDATE_PERSONAL_INFORMATION = 'PRE_REGISTRATION_UPDATE_PERSONAL_INFORMATION';
export const UPDATE_COLLECTION_ATTEMPT_STATUS = 'PRE_REGISTRATION.UPDATE_COLLECTION_ATTEMPT_STATUS';

export const updateSelfPaymentFlag = (flag: boolean) => ({
  type: UPDATE_SELF_PAYMENT_FLAG,
  payload: { data: flag }
});

export const resetPreRegistration = () => ({
  type: PRE_REGISTRATION_RESET
});

export const preRegistrationDestroySession = payload => ({
  type: PRE_REGISTRATION_DESTROY_SESSION,
  payload
});

export const updateChecklistStep = (payload: CheckListItem) => ({
  type: UPDATE_PRE_REGISTRATION_CHECKLIST_STEP,
  payload
});

export const updateSkipPaymentFlag = (payload: UpdateSkipPaymentFlagPayload) => ({
  type: UPDATE_SKIP_PAYMENT_FLAG,
  payload
});

const phoneTypeMap = {
  CELLPHONE: 'MOBILE',
  HOMEPHONE: 'HOME'
};

export function patientInfoToConsumer(patientInfo: PatientInfo): Consumer {
  return {
    cernerPersonId: patientInfo.cernerPersonId,
    firstName: patientInfo.firstName,
    lastName: patientInfo.lastName,
    middleName: patientInfo.middleName,
    dateOfBirth: patientInfo.dateOfBirth,
    emails: patientInfo.emails.map((email: { address: string; type: string }, idx: number) => {
      return {
        primary: idx === 0,
        value: email.address,
        verified: true,
        type: email.type === 'HOMEEMAIL' ? 'HOME' : email.type
      };
    }),
    phones: patientInfo.phones
      .filter(phone => phone.type === 'CELLPHONE')
      .map((phone: PatientPhone, idx: number) => {
        return {
          primary: idx === 0,
          value: cleanPhone(`${phone.areaCode}${phone.phoneNumber}`),
          verified: true,
          type: phoneTypeMap[phone.type]
        };
      }),
    addresses: patientInfo.addresses.map((address, idx) => {
      return {
        streetAddress: address.address1,
        streetAddress2: address.address2,
        city: address.city,
        state: address.state,
        postalCode: address.zip,
        country: 'US',
        type: address.type === 'HOMEADDR' ? 'HOME' : address.type,
        primary: idx === 0
      };
    }),
    age: getAge(patientInfo.dateOfBirth),
    ethnicity: patientInfo.demographics.ethnicity,
    gender: patientInfo.gender
  } as Consumer;
}

export const SEND_PIN = 'PRE_REGISTRATION_SEND_PIN';
export const SEND_PIN_SUCCESS = 'PRE_REGISTRATION_SEND_PIN_SUCCESS';
export const SEND_PIN_FAIL = 'PRE_REGISTRATION_SEND_PIN_FAIL';
export const sendPreRegPin = (props: SendPinProps, patientInfo: PatientInfo): Thunk => dispatch => {
  const { type, phone, email } = props;
  const allowedTypes = ['email', 'sms'];

  if (
    allowedTypes.indexOf(type) < 0 ||
    (type === 'email' && !email) ||
    (type === 'sms' && !phone)
  ) {
    return false;
  }

  const consumer = patientInfoToConsumer(patientInfo);
  const pendingConsumer =
    type === 'email' ? updateConsumerEmail(consumer, email) : updateConsumerPhone(consumer, phone);

  return dispatch({
    type: SEND_PIN,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        url: '/iddataweb/public/sendotpcode',
        data: {
          consumer: {
            registrationStatus: 'Contact',
            ...pendingConsumer
          },
          consumerVerification: {
            sendOTP: true,
            verifyUser: false,
            verificationStatus: 'SendOTP',
            otpType: type
          }
        }
      }
    }
  });
};

export const VALIDATE_PIN = 'PRE_REGISTRATION_VALIDATE_PIN';
export const VALIDATE_PIN_SUCCESS = 'PRE_REGISTRATION_VALIDATE_PIN_SUCCESS';
export const VALIDATE_PIN_FAIL = 'PRE_REGISTRATION_VALIDATE_PIN_FAIL';
export const validatePreRegPin = (props: ValidatePinProps, patientInfo: PatientInfo): Thunk => (
  dispatch: RootDispatch
) => {
  const { email, phone, pin, type } = props;
  const allowedTypes = ['email', 'sms'];

  if (
    allowedTypes.indexOf(type) < 0 ||
    (type === 'email' && !email) ||
    (type === 'sms' && !phone)
  ) {
    return false;
  }

  const consumer = patientInfoToConsumer(patientInfo);
  const pendingConsumer =
    type === 'email' ? updateConsumerEmail(consumer, email) : updateConsumerPhone(consumer, phone);

  if (!pendingConsumer.emails[0].value) {
    throw new Error('validatePreRegPin requires an email');
  }
  return dispatch({
    type: VALIDATE_PIN,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        url: '/iddataweb/public/validateotpcode',
        data: {
          consumer: {
            registrationStatus: 'Contact',
            ...pendingConsumer
          },
          consumerVerification: {
            sendOTP: false,
            verifyUser: false,
            verificationStatus: 'ValidateOTP',
            otpType: type,
            validateOTP: true,
            pin
          }
        }
      }
    }
  });
};
